import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useEntityStore, usePortfolioPrefsStore } from '../store';
import { subscriptionsAPI } from '../lib/api';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';

// "What are we paying, and who pays it" -- live subscriptions grouped
// Jurisdiction -> Entity -> Vendor (GET /subscriptions/by-jurisdiction).
//
// Fan, 2026-09-03: one aggregated figure per group, in the currency the toggle
// selects, instead of a stack of per-currency lines. The backend converts with
// the same spot rates the bank-balance cards use and returns the native
// per-currency evidence alongside it (shown on hover/expanded rows), plus an
// explicit unconvertible list for any currency with no known rate, which is
// excluded from the aggregate rather than added at 1:1.

const SYMBOLS = { GBP: '\u00A3', EUR: '\u20AC', USD: '$', DKK: 'kr ' };

function money(value, currency) {
  const n = Number(value || 0);
  return `${SYMBOLS[currency] || `${currency} `}${n.toLocaleString('en-GB', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

// One aggregated figure in the display currency, with the native
// per-currency amounts behind it as secondary text (evidence, not a second
// headline) and any unconvertible currency called out.
function TotalsLine({ group, currency }) {
  if (!group) return <span className="text-muted-foreground text-sm">--</span>;
  const natives = group.native || [];
  const unconvertible = group.unconvertible || [];
  return (
    <span className="text-right">
      <span className="font-mono text-sm whitespace-nowrap">
        {money(group.monthly, currency)}
        <span className="text-muted-foreground text-xs">{' '}/mo</span>
      </span>
      {natives.length > 1 && (
        <span className="block text-xs text-muted-foreground font-mono">
          {natives.map((n) => money(n.monthly, n.currency)).join(' + ')}
        </span>
      )}
      {unconvertible.length > 0 && (
        <span className="block text-xs text-amber-700 dark:text-amber-500 font-mono">
          + {unconvertible.map((u) => money(u.monthly, u.currency)).join(', ')} (no FX rate)
        </span>
      )}
    </span>
  );
}

export const SPEND_TYPE_TABS = [
  { key: 'all', label: 'All recurring' },
  { key: 'subscription', label: 'Subscriptions' },
  { key: 'rent', label: 'Other fixed costs' },
  { key: 'unclassified', label: 'Needs review' },
  { key: 'excluded', label: 'Not counted' },
];

export const SPEND_TYPE_BADGE = {
  subscription: { label: 'Subscription', className: 'bg-primary/10 text-primary' },
  rent: { label: 'Other fixed cost', className: 'bg-blue-500/10 text-blue-700 dark:text-blue-400' },
  unclassified: { label: 'Needs review', className: 'bg-amber-500/10 text-amber-700 dark:text-amber-500' },
  excluded: { label: 'Not counted', className: 'bg-muted text-muted-foreground' },
};

// Plain-English reason behind a row's type, shown as the badge tooltip.
export function reasonText(v) {
  if (v.spend_type === 'excluded') return v.spend_type_label || 'Not a recurring cost';
  const r = String(v.spend_type_reason || '');
  if (r.startsWith('service_vendor')) return 'Known software or service vendor';
  if (r.startsWith('utility_or_insurance')) return 'Utility, telecom or insurance';
  if (r === 'registered_rent_payee') return 'Registered landlord';
  if (r.startsWith('rent_payment_reference')) return 'Payment reference says rent';
  if (r === 'property_cost_category') return 'Bank line categorised as a property cost';
  if (r.startsWith('rent')) return 'Rent, office or council';
  if (r === 'operator_override') return 'Set by hand';
  return 'No rule matched yet';
}

function EntityBlock({ entity, currency }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t first:border-t-0" data-testid={`subs-entity-${entity.entity_id || 'unassigned'}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-3 py-2 text-left hover:bg-muted/50"
      >
        <span className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <span className="font-medium truncate">{entity.entity_name}</span>
          <Badge variant="outline" className="shrink-0">{entity.vendor_count} vendors</Badge>
        </span>
        <TotalsLine group={entity} currency={currency} />
      </button>
      {open && (
        <div className="px-3 pb-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-muted-foreground text-xs">
                <th className="text-left py-1 pr-3">Vendor</th>
                <th className="text-left py-1 pr-3">Type</th>
                <th className="text-right py-1 pr-3">Monthly</th>
                <th className="text-right py-1 pr-3">Payments (3 mo)</th>
                <th className="text-right py-1 pr-3">Last payment</th>
              </tr>
            </thead>
            <tbody>
              {entity.vendors.map((v) => (
                <tr
                  key={`${v.vendor}-${v.currency}`}
                  className="border-b border-border/50"
                  data-testid={`subs-vendor-${entity.entity_id || 'unassigned'}-${v.vendor}`}
                >
                  <td className="py-1 pr-3">
                    {v.vendor}
                      {v.spend_type === 'excluded' && v.spend_type_label ? (
                      <span className="block text-xs text-muted-foreground">{v.spend_type_label}</span>
                    ) : null}
                  </td>
                  <td className="py-1 pr-3">
                    <Badge
                      variant="outline"
                      className={cn('text-[10px] px-1.5 py-0', (SPEND_TYPE_BADGE[v.spend_type] || {}).className)}
                      title={reasonText(v)}
                    >
                      {(SPEND_TYPE_BADGE[v.spend_type] || {}).label || v.spend_type}
                    </Badge>
                  </td>
                  <td className="py-1 pr-3 text-right font-mono">
                    {v.unconvertible ? (
                      <span className="text-amber-700 dark:text-amber-500" title="No FX rate: shown in its own currency and excluded from the totals">
                        {money(v.monthly_native, v.currency)}
                      </span>
                    ) : money(v.monthly, currency)}
                  </td>
                  <td className="py-1 pr-3 text-right text-muted-foreground font-mono">{v.payments ?? '--'}</td>
                  <td className="py-1 pr-3 text-right text-muted-foreground">{v.last_payment || '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function SubscriptionsByJurisdictionTab({ scope }) {
  const selectedEntity = useEntityStore((s) => s.selectedEntity);
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const [spendType, setSpendType] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const entityParam = selectedEntity && selectedEntity !== 'all' && !String(selectedEntity).startsWith('group:')
    ? selectedEntity
    : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await subscriptionsAPI.byJurisdiction({
        entityId: entityParam,
        scope: scope === 'personal' ? 'personal' : 'entities',
        displayCurrency,
        spendType: spendType === 'all' ? undefined : spendType,
      });
      setData(res.data);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [entityParam, scope, displayCurrency, spendType]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading run rate...
      </div>
    );
  }
  if (error) {
    return <p className="text-sm text-destructive py-4" data-testid="subs-jurisdiction-error">{error}</p>;
  }

  const jurisdictions = data?.jurisdictions || [];

  return (
    <div className="space-y-4" data-testid="subs-by-jurisdiction">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex h-8 items-center rounded-lg bg-muted p-0.5" data-testid="subs-spend-type-filter">
          {SPEND_TYPE_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setSpendType(t.key)}
              className={cn(
                'px-2.5 py-1 text-xs font-medium rounded-md transition-all',
                spendType === t.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
              data-testid={`subs-spend-type-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-sm text-muted-foreground">
          {spendType === 'excluded'
            ? `${data?.subscription_count ?? 0} payees paid out but left out of the totals, with the reason.`
            : `${data?.subscription_count ?? 0} payees paid at least twice in the last 3 full months (or a known vendor), monthly average from the bank.`}
        </p>
      </div>
      {jurisdictions.length === 0 && (
        <p className="text-sm text-muted-foreground">Nothing in this view.</p>
      )}
      {jurisdictions.map((j) => (
        <Card key={j.jurisdiction} data-testid={`subs-jurisdiction-${j.jurisdiction}`}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">{j.jurisdiction_label}</CardTitle>
                <CardDescription>{j.entity_count} entities</CardDescription>
              </div>
              <div className={cn('text-right')}>
                <TotalsLine group={j} currency={data?.display_currency || displayCurrency} />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="rounded-md border">
              {j.entities.map((e) => (
                <EntityBlock
                  key={e.entity_id || 'unassigned'}
                  entity={e}
                  currency={data?.display_currency || displayCurrency}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
